import React, { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GoogleRecaptcha from "@/components/GoogleRecaptcha";
import { insertResumeSchema } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { usePublicConfig } from "@/lib/publicConfig";
import { trackEvent } from "@/lib/analytics";
import type { JobOpening, InsertResume } from "@shared/schema";
import { z } from "zod";
import { Link } from "wouter";

// ✅ schema
const resumeFormSchema = insertResumeSchema.extend({
  resumeUrl: z.string().min(1, "Resume file is required"),
});
type ResumeFormValues = z.infer<typeof resumeFormSchema>;

// ✅ safe parse helper
const safeParseSkills = (skills: unknown): string[] => {
  try {
    if (Array.isArray(skills)) {
      return skills.filter((x): x is string => typeof x === "string");
    }
    if (typeof skills === "string") {
      const parsed: unknown = JSON.parse(skills);
      return Array.isArray(parsed)
        ? parsed.filter((x): x is string => typeof x === "string")
        : [];
    }
    return [];
  } catch {
    return [];
  }
};

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export default function Careers() {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);

  const formWrapRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: publicConfig, isLoading: isPublicConfigLoading } = usePublicConfig();
  const recaptcha = publicConfig?.recaptcha ?? { enabled: false, siteKey: "" };

  const { data: jobs, isLoading } = useQuery<JobOpening[]>({
    queryKey: ["/api/jobs"],
  });

  const jobsList = useMemo(() => jobs ?? [], [jobs]);

  const form = useForm<ResumeFormValues>({
    resolver: zodResolver(resumeFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      position: "",
      resumeUrl: "",
    },
    mode: "onTouched",
  });

  const resumeMutation = useMutation({
    mutationFn: async (data: InsertResume & { captchaToken?: string }) => {
      const response = await apiRequest("POST", "/api/resumes", data);
      return response.json();
    },
    onSuccess: () => {
      trackEvent("generate_lead", {
        form: "careers",
        position: form.getValues("position") || "unspecified",
      });
      toast({
        title: "Application Submitted ✅",
        description: "We’ve received your application. Our team will reach out soon.",
      });
      form.reset();
      setCaptchaResetSignal((value) => value + 1);
      setActiveJobId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/resumes"] });
    },
    onError: () => {
      setCaptchaResetSignal((value) => value + 1);
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ResumeFormValues) => {
    if (recaptcha.enabled && !captchaToken) {
      toast({
        title: "Security Check Pending",
        description: "Please wait for the security verification before submitting your application.",
        variant: "destructive",
      });
      return;
    }

    resumeMutation.mutate({
      ...data,
      captchaToken: recaptcha.enabled ? captchaToken : undefined,
    });
  };

  async function uploadResumeFile(file: File) {
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("resume", file);

      const res = await fetch("/api/resumes/upload", {
        method: "POST",
        body: fd,
        credentials: "include",
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Upload failed");
      }

      const json: unknown = await res.json();
      const url = (json as { url?: string })?.url;
      if (!url) throw new Error("Invalid upload response");
      return url;
    } finally {
      setUploading(false);
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    uploadResumeFile(file)
      .then((url) => {
        form.setValue("resumeUrl", url, { shouldValidate: true });
        toast({ title: "Resume Uploaded ✅", description: `"${file.name}" uploaded.` });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Failed to upload resume";
        toast({ title: "Upload Error", description: msg, variant: "destructive" });
      });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    uploadResumeFile(file)
      .then((url) => {
        form.setValue("resumeUrl", url, { shouldValidate: true });
        toast({ title: "Resume Uploaded ✅", description: `"${file.name}" uploaded.` });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Upload failed";
        toast({ title: "Upload Error", description: msg, variant: "destructive" });
      });
  };

  const onClickApply = (job: JobOpening) => {
    trackEvent("apply_click", { position: job.title });
    setActiveJobId(job.id);
    form.setValue("position", job.title, { shouldValidate: true, shouldDirty: true });
    requestAnimationFrame(() => {
      formWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    toast({ title: "Applying for", description: job.title });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFB] text-slate-900 dark:bg-gray-900 dark:text-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');
        .font-roboto{font-family:'Roboto',system-ui,-apple-system,Segoe UI,Arial}
        @keyframes fadeUp { 0%{opacity:0;transform:translateY(16px)} 100%{opacity:1;transform:translateY(0)} }
        .anim-fadeUp{animation:fadeUp .65s cubic-bezier(.2,.9,.2,1) both}

        /* Grid pattern with CSS variables for dark mode */
        .grid-pattern {
          background-image: 
            linear-gradient(90deg, var(--grid-color) 1px, transparent 1px),
            linear-gradient(0deg, var(--grid-color) 1px, transparent 1px);
          background-size: 64px 64px;
        }
        :root {
          --grid-color: rgba(2, 6, 23, 0.05); /* slate-950 with low opacity */
        }
        @media (prefers-color-scheme: dark) {
          :root {
            --grid-color: rgba(255, 255, 255, 0.05);
          }
        }

        /* Soft premium noise – hidden in dark mode because multiply doesn't work well */
        .noise{
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.12'/%3E%3C/svg%3E");
          mix-blend-mode: multiply;
        }
      `}</style>

      <Navigation />

      {/* HERO (simple & sober) */}
      <section className="relative overflow-hidden font-roboto">
        {/* Background base layer */}
        <div className="absolute inset-0 bg-[#FAFAFB] dark:bg-gray-900" />

        {/* Subtle grid pattern using CSS variable */}
        <div className="absolute inset-0 grid-pattern opacity-[0.28] dark:opacity-20" />

        {/* Noise overlay – hidden in dark mode */}
        <div className="absolute inset-0 noise opacity-[0.35] dark:hidden" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-10 sm:pt-20 sm:pb-14">
          <div className="mx-auto max-w-3xl text-center anim-fadeUp">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <span className="h-2 w-2 rounded-full bg-slate-900 dark:bg-blue-400" />
              <span className="text-xs font-semibold tracking-[0.22em] uppercase text-slate-700 dark:text-gray-300">
                Careers
              </span>
              <span className="h-2 w-2 rounded-full bg-slate-300 dark:bg-gray-500" />
            </div>

            <h1 className="mt-7 text-4xl sm:text-5xl font-black leading-[1.05] text-slate-900 dark:text-white">
              Build Your Career <span className="text-slate-500 dark:text-gray-400">With Us</span>
            </h1>

            <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed dark:text-gray-300">
              A calm, focused team. High ownership. High quality. Apply in under 2 minutes.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                className="rounded-2xl w-[200px] h-[60px] px-7 py-6 text-base font-extrabold bg-slate-900 text-white hover:bg-slate-950 shadow-[0_16px_50px_rgba(2,6,23,0.14)] dark:bg-blue-600 dark:hover:bg-blue-700 dark:shadow-none"
                onClick={() => formWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                Apply Now
              </Button>
              <a
                href="#openings"
                className="rounded-2xl w-[200px] h-[60px] px-7 py-4 font-extrabold border border-slate-200 bg-white hover:bg-slate-50 shadow-sm text-slate-900 text-center dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
              >
                View Openings
              </a>
            </div>

            {/* premium strip */}
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { k: "Avg Response", v: "24 hrs" },
                { k: "Process", v: "2 rounds" },
                { k: "Culture", v: "Ownership" },
              ].map((x) => (
                <div
                  key={x.k}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="text-xs font-semibold text-slate-500 dark:text-gray-400">{x.k}</div>
                  <div className="text-lg font-black text-slate-900 dark:text-white">{x.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CONTENT */}
          {isLoading ? (
            <section className="mt-14">
              <div className="space-y-6">
                <Skeleton className="h-10 w-72 dark:bg-gray-700" />
                <Skeleton className="h-6 w-[520px] dark:bg-gray-700" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
                  <div className="lg:col-span-2 space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-44 w-full rounded-3xl dark:bg-gray-700" />
                    ))}
                  </div>
                  <Skeleton className="h-[560px] w-full rounded-3xl dark:bg-gray-700" />
                </div>
              </div>
            </section>
          ) : (
            <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1.12fr_.88fr] gap-8 lg:gap-10">
              {/* LEFT: JOBS */}
              <div id="openings" className="anim-fadeUp" style={{ animationDelay: "120ms" }}>
                <div className="flex items-end justify-between gap-6">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
                      Current Openings
                    </h2>
                    <p className="mt-1 text-slate-600 dark:text-gray-300">
                      Roles that value craftsmanship and shipping quality work.
                    </p>
                  </div>
                  <span className="hidden sm:inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    Updated regularly
                  </span>
                </div>

                <div className="mt-6 space-y-5">
                  {jobsList.length === 0 ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                      <p className="text-slate-900 font-black dark:text-white">No openings right now.</p>
                      <p className="mt-1 text-slate-600 text-sm dark:text-gray-300">
                        You can still submit your resume — we’ll reach out when a matching role opens.
                      </p>
                    </div>
                  ) : (
                    jobsList.map((job) => {
                      const isActive = activeJobId === job.id;
                      return (
                        <div
                          key={job.id}
                          className={cn(
                            "group relative overflow-hidden rounded-3xl border bg-white",
                            "shadow-[0_10px_30px_rgba(2,6,23,0.06)]",
                            "transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(2,6,23,0.10)]",
                            "dark:border-gray-700 dark:bg-gray-800 dark:shadow-none dark:hover:shadow-lg dark:hover:shadow-black/20",
                            isActive ? "border-slate-900 dark:border-blue-500" : "border-slate-200"
                          )}
                        >
                          {/* subtle left bar */}
                          <div
                            className={cn(
                              "absolute left-0 top-0 h-full w-[5px]",
                              isActive ? "bg-slate-900 dark:bg-blue-500" : "bg-slate-200 dark:bg-gray-600"
                            )}
                          />
                          <div className="relative p-6 sm:p-7">
                            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
                              <div className="min-w-0">
                                <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                                  {job.title}
                                </h3>

                                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                  {[job.location, job.type, job.experience].map((t, i) => (
                                    <span
                                      key={i}
                                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-bold text-slate-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>

                                <p className="mt-4 text-slate-600 leading-relaxed dark:text-gray-300">
                                  {job.description}
                                </p>

                                <div className="mt-4 flex flex-wrap gap-2">
                                  {safeParseSkills(job.skills).slice(0, 12).map((skill, i) => (
                                    <span
                                      key={`${job.id}-skill-${i}`}
                                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50 transition dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
                                <Button
                                  type="button"
                                  onClick={() => onClickApply(job)}
                                  className="rounded-2xl px-6 py-5 font-black bg-slate-900 text-white hover:bg-slate-950 shadow-[0_16px_45px_rgba(2,6,23,0.14)] dark:bg-blue-600 dark:hover:bg-blue-700 dark:shadow-none"
                                >
                                  Apply Now
                                </Button>
                                <div className="text-xs font-semibold text-slate-500 dark:text-gray-400">
                                  Quick apply • 2 minutes
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* RIGHT: FORM */}
              <div ref={formWrapRef} className="anim-fadeUp" style={{ animationDelay: "220ms" }}>
                <div className="lg:sticky lg:top-6">
                  <div className="rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_55px_rgba(2,6,23,0.08)] dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
                    <div className="p-7 sm:p-8">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                            Submit Your Resume
                          </h3>
                          <p className="text-slate-600 text-sm mt-1 dark:text-gray-300">
                            We usually respond within 48 hours.
                          </p>
                        </div>
                        <span className="hidden sm:inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
                          Secure Upload
                        </span>
                      </div>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-900 font-black dark:text-white">Full Name</Label>
                            <Input
                              className="mt-2 h-12 rounded-2xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:ring-gray-500"
                              placeholder="John Smith"
                              {...form.register("fullName")}
                            />
                          </div>
                          <div>
                            <Label className="text-slate-900 font-black dark:text-white">Phone</Label>
                            <Input
                              className="mt-2 h-12 rounded-2xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:ring-gray-500"
                              placeholder="+91 9XXXXXXXXX"
                              {...form.register("phone")}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-slate-900 font-black dark:text-white">Email</Label>
                          <Input
                            type="email"
                            className="mt-2 h-12 rounded-2xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:ring-gray-500"
                            placeholder="john@example.com"
                            {...form.register("email")}
                          />
                        </div>

                        {/* Upload */}
                        <div>
                          <Label className="text-slate-900 font-black dark:text-white">Upload Resume</Label>
                          <div
                            className={cn(
                              "mt-2 rounded-3xl border-2 border-dashed p-6 text-center transition-all duration-300",
                              "bg-slate-50 dark:bg-gray-700/50",
                              dragActive ? "border-slate-900 dark:border-blue-500" : "border-slate-200 hover:border-slate-300 dark:border-gray-600 dark:hover:border-gray-500"
                            )}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                          >
                            <input
                              type="file"
                              id="resume-upload"
                              accept=".pdf,.doc,.docx"
                              onChange={handleFileSelect}
                              className="hidden"
                            />

                            <label htmlFor="resume-upload" className="cursor-pointer">
                              <div className="flex flex-col items-center gap-3">
                                <div className="grid place-items-center h-12 w-12 rounded-2xl bg-slate-900 text-white shadow-sm dark:bg-blue-600">
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6}
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                  </svg>
                                </div>

                                <div className="space-y-1">
                                  <div className="text-sm font-black text-slate-900 dark:text-white">
                                    {form.watch("resumeUrl") ? "Resume uploaded successfully" : "Drag & drop or browse"}
                                  </div>
                                  <div className="text-xs text-slate-600 font-semibold dark:text-gray-400">
                                    PDF, DOC, DOCX up to 5MB
                                  </div>
                                </div>
                              </div>
                            </label>

                            {uploading && (
                              <div className="mt-4 text-sm font-black text-slate-700 dark:text-gray-200">
                                Uploading...
                              </div>
                            )}

                            {form.watch("resumeUrl") && !uploading && (
                              <div className="mt-4">
                                <button
                                  type="button"
                                  onClick={() => form.setValue("resumeUrl", "", { shouldValidate: true })}
                                  className="text-xs font-black text-slate-700 hover:text-slate-900 underline underline-offset-4 dark:text-gray-300 dark:hover:text-white"
                                >
                                  Remove file
                                </button>
                              </div>
                            )}
                          </div>

                          {form.formState.errors.resumeUrl && (
                            <p className="text-red-500 text-xs mt-2">
                              {form.formState.errors.resumeUrl.message}
                            </p>
                          )}
                        </div>

                        <GoogleRecaptcha
                          enabled={recaptcha.enabled}
                          siteKey={recaptcha.siteKey}
                          resetSignal={captchaResetSignal}
                          onChange={setCaptchaToken}
                        />

                        <Button
                          type="submit"
                          disabled={
                            resumeMutation.isPending ||
                            uploading ||
                            isPublicConfigLoading ||
                            (recaptcha.enabled && !captchaToken)
                          }
                          className="w-full h-12 rounded-2xl font-black bg-slate-900 text-white hover:bg-slate-950 shadow-[0_16px_45px_rgba(2,6,23,0.14)] disabled:opacity-60 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700 dark:shadow-none"
                        >
                          {resumeMutation.isPending ? "Submitting..." : "Submit Application"}
                        </Button>

                        <p className="text-xs text-slate-600 text-center pt-1 font-semibold dark:text-gray-400">
                          By applying, you agree to our{" "}
                          <Link
                            href="/privacy-policy"
                            className="font-black text-slate-900 underline underline-offset-4 dark:text-white dark:hover:text-gray-200"
                          >
                            Privacy Policy
                          </Link>
                        </p>
                      </form>

                      <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-700/30">
                        <p className="text-xs text-slate-600 font-semibold leading-relaxed dark:text-gray-300">
                          Tip: Select an opening & click <span className="font-black text-slate-900 dark:text-white">Apply Now</span>{" "}
                          to auto-fill the position.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* END */}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
